import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { UtilsService } from '../../shared/utils.service';
import { RBACService } from '../../shared/rbac.service';
import { TaskAnalysesEditComponent } from '../task-analyses-edit/task-analyses-edit.component';
import { TaskAnalysesViewComponent } from '../task-analyses-view/task-analyses-view.component';
import {SiteModel} from "../../models/site.model";
import {SitesSelectorComponent} from "../../shared/sites-selector/sites-selector.component";
import {TaskAnalysisModel} from "../../models/task-analysis.model";

@Component({
    selector: 'app-task-analyses',
    templateUrl: './task-analyses.component.html',
    styleUrls: ['./task-analyses.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskAnalysesComponent implements OnInit, AfterViewInit {
  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Build displayedColumns dynamically: NOT as a static array.
  displayedColumns: string[] = [];

  dataSource = new TaskAnalysisDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TEMPLATES_TASK_ANALYSIS_DOCUMENTS, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TEMPLATES_TASK_ANALYSIS_DOCUMENTS, RBACService.RBAC_A.A_VIEW)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TEMPLATES_TASK_ANALYSIS_DOCUMENTS, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TEMPLATES_TASK_ANALYSIS_DOCUMENTS, RBACService.RBAC_A.A_DELETE)
    ], true);

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to access task analysis documents.');
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [
      'id',
      'sites',
      'task_name',
      'date_created',
      'actions'
    ];
    if (this.canDelete) {
      this.displayedColumns.unshift('select');
    }

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.getData();
    }
  }

  /**
   * Handles the add operation by opening the edit dialog for creating a new task analysis.
   */
  onAdd(): void {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create task analysis documents.');
      return;
    }
    this.openEditDialog();
  }

  /**
   * Handles the editing process by opening a dialog for editing task analyses.
   */
  onEdit(id?: number | string): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit task analysis documents.');
      return;
    }
    this.openEditDialog(id);
  }

  /**
   * Shared private method to open the edit dialog for both create and edit operations.
   */
  private openEditDialog(id?: number | string): void {
    this.utils.showComponentDialog(TaskAnalysesEditComponent,
      {
        task_analysis_id: id,
        feature: RBACService.RBAC_F.F_TEMPLATES_TASK_ANALYSIS_DOCUMENTS
      },
      {
        width: '700px'
      },
      (success: boolean): void => {
        if (!success) { return; }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      }
    );
  }

  onView(id: number) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view task analysis documents.');
      return;
    }
    this.utils.showComponentDialog(
      TaskAnalysesViewComponent,
      id,
      { width: '700px' },
      (success: boolean) => {
        if (!success) { return; }
        this.dataSource.getData(true);
      }
    );
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC by its corresponding date column.
      if ( this.sort.active == 'date_created_UTC' ) {
        this.dataSource.sort_by = 'date_created';
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onClearSearch() {
    this.dataSource.search = '';
    this.dataSource.getData(true);
  }

  onRemove(id: number) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete task analysis documents.');
      return;
    }
    const singular = this.utils.getLangTerm('task_analysis_documents.singular', 'Task Analysis Document');
    this.utils.showModal(
      `Remove ${singular}`,
      `Are you sure you want to remove this ${singular}?`,
      () => {
        this.api.makeRequest(
          'delete',
          'v1/task-analysis/' + id
        )
        .then((response) => {
          this.utils.showToast(`The ${singular} was removed.`);
          this.dataSource.selection.deselect(id);
          this.dataSource.getData();
        })
        .catch((error) => {
          this.utils.showModal('Error', error.error?.message);
        });
      }
    );
  }

  onDeleteSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete task analysis documents.');
      return;
    }
    const singular = this.utils.getLangTerm('task_analysis_documents.singular', 'Task Analysis Document');
    this.utils.showModal(
      `Remove ${singular}`,
      `Are you sure you want to remove the selected ${singular}?`,
      () => {
        this.api.makeRequest(
          'delete',
          'v1/task-analysis/' + this.dataSource.selection.selected.join(',')
        )
        .then((response) => {
          this.utils.showToast(`The selected ${singular} was removed`);
          this.dataSource.selection.clear();
          this.dataSource.getData();
        })
        .catch((error) => {
          this.utils.showModal('Error', error.error?.message);
        });
      }
    );
  }

  onSelectSites() {
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: this.dataSource.site_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if ( typeof results !== 'undefined' ) {
          this.dataSource.site_ids = results;
          this.dataSource.getData(true);
        }
      }
    );
  }

  /**
   * Format the text display for associated sites.
   * Examples:
   *  - Head Office
   *  - 0 Sites
   *  - 1 Site (If name is not accessible otherwise the name of the site)
   *  - 2 Sites
   *  - Etc.
   * @param form
   */
  formatSitesListColumnText(task_analysis: TaskAnalysisModel) {
    // Check if we have one user.
    if ( task_analysis.sites_count == 1 ) {
      // Check if we have details of one user.
      if ( task_analysis.sites[0] ) {
        return task_analysis.sites[0].name;
      }
      // Respond with a generic 1 User response.
      return task_analysis.sites_count + ' ' + this.utils.getLangTerm('parent-child-sites-combined.singular', 'Site');
    }
    // Respond with the users count.
    return task_analysis.sites_count + ' ' + this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites');
  }

  /**
   * The tooltip text is only used if there is more than 1 site.
   * @param form
   */
  formatSitesListColumnTooltip(task_analysis: TaskAnalysisModel) {
    if ( task_analysis.sites_count > 1 ) {
      const site_names: string[] = task_analysis.sites.map((site: SiteModel) => {
        return site.name;
      });
      return site_names.join(`\n`);
    }
    return '';
  }
}

export class TaskAnalysisDataSource extends CustomDataSource {
  // record sorting and direction
  sort_by = 'task_name';
  sort_order = 'asc';

  site_ids: number[] = [];

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI('task-analysis', resetOffset, () => {}, {
      site_ids: this.site_ids.join(',')
    });
  }
}
